import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { memo } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import type { CommissionRateFormData } from "../types/commission-rate.types";

export const RATE_TYPE_LABELS: Record<string, string> = {
  SUIT: "Terno",
  SHOES: "Sapatos",
  GOWN: "Beca",
  GRADUATION: "Formatura",
};

interface CommissionRateFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  loading: boolean;
  form: UseFormReturn<CommissionRateFormData>;
  onClose: () => void;
  onSubmit: (data: CommissionRateFormData) => void;
}

export const CommissionRateFormDialog = memo(function CommissionRateFormDialog({
  open,
  mode,
  loading,
  form,
  onClose,
  onSubmit,
}: CommissionRateFormDialogProps) {
  const chargeType = useWatch({ control: form.control, name: "chargeType" });
  const selectedType = useWatch({ control: form.control, name: "type" });

  const isFixedOnly = selectedType === "SHOES";
  const isPercentageOnly =
    selectedType === "SUIT" ||
    selectedType === "GOWN" ||
    selectedType === "GRADUATION";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Nova Taxa de Comissão"
              : "Editar Taxa de Comissão"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Cadastre uma nova taxa de comissão"
              : "Atualize as informações da taxa de comissão"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Comissão Padrão Terno"
                      disabled={loading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo do produto/aluguel *</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        const val = e.target.value;
                        if (val === "SHOES") {
                          form.setValue("chargeType", "fixed");
                          form.setValue("rate", "");
                        } else if (
                          val === "SUIT" ||
                          val === "GOWN" ||
                          val === "GRADUATION"
                        ) {
                          form.setValue("chargeType", "percentage");
                          form.setValue("value", "");
                        }
                      }}
                      className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={loading}
                    >
                      {Object.entries(RATE_TYPE_LABELS).map(
                        ([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="chargeType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de cobrança *</FormLabel>
                  {isFixedOnly && (
                    <p className="text-xs text-muted-foreground">
                      Sapatos só permitem cobrança por valor fixo.
                    </p>
                  )}
                  {isPercentageOnly && (
                    <p className="text-xs text-muted-foreground">
                      Este tipo só permite cobrança percentual.
                    </p>
                  )}
                  <FormControl>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange("percentage");
                          form.setValue("value", "");
                        }}
                        disabled={loading || isFixedOnly}
                        className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          field.value === "percentage"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                      >
                        Percentual
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange("fixed");
                          form.setValue("rate", "");
                        }}
                        disabled={loading || isPercentageOnly}
                        className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          field.value === "fixed"
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                      >
                        Fixo
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {chargeType === "percentage" && (
              <FormField
                control={form.control}
                name="rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Percentual (%) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        placeholder="Ex: 10"
                        disabled={loading}
                        {...field}
                        value={field.value === "" ? "" : field.value}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {chargeType === "fixed" && (
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor fixo (R$) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Ex: 50.00"
                        disabled={loading}
                        {...field}
                        value={field.value === "" ? "" : field.value}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="initialDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data inicial *</FormLabel>
                  <FormControl>
                    <Input type="date" disabled={loading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="finalDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data final</FormLabel>
                  <FormControl>
                    <Input type="date" disabled={loading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : mode === "create"
                    ? "Criar Taxa"
                    : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
